import React, { useEffect } from 'react';
import { Grid, Tooltip, Typography } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  useTable,
  usePagination,
  useSortBy,
  useFilters,
  useBlockLayout,
  useResizeColumns,
} from 'react-table-v7';
import { styles } from '../../containers/common/history-styles';

const LIMIT_TO_SHOW_CELL_TOOLTIP = 24;

const getSanitizedValue = value => {
  let sanitized = String(value);
  let previous;
  do {
    previous = sanitized;
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  } while (sanitized !== previous);
  return sanitized.trim();
};

const HistoryTable = props => {
  const { ptgotNoteHistory, classes } = props;

  const DefaultColumnFilter = ({ column: { filterValue, preFilteredRows, setFilter } }) => {
    const count = preFilteredRows.length;

    return (
      <input
        value={filterValue || ''}
        onChange={e => {
          setFilter(e.target.value || undefined);
        }}
        className={classes.filterInput}
      />
    );
  };

  const ReactTableComp = ({ columns, data }) => {
    const filterTypes = React.useMemo(
      () => ({
        text: (rows, id, filterValue) => {
          return rows.filter(row => {
            const rowValue = row.values[id];
            return rowValue !== undefined
              ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
              : true;
          });
        },
      }),
      [],
    );

    const defaultColumn = React.useMemo(
      () => ({
        Filter: DefaultColumnFilter,
      }),
      [],
    );

    const renderCell = cell => {
      if (cell?.value !== null) {
        const cellRender = cell.render('Cell');
        const cellValue = cell.value;
        return (
          <td
            {...cell.getCellProps()}
            className={classes.tbodyTr}
            style={{
              wordWrap: 'break-word',
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              maxWidth: '797px',
              minWidth: '797px',
            }}
          >
            {cellValue?.length > LIMIT_TO_SHOW_CELL_TOOLTIP ? (
              <Tooltip arrow title={cellValue}>
                <Typography>{cellRender}</Typography>
              </Tooltip>
            ) : (
              <Typography>{cellRender}</Typography>
            )}
          </td>
        );
      }
      return null;
    };

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      row,
      page,
      canPreviousPage,
      canNextPage,
      pageOptions,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state: { pageIndex, pageSize },
    } = useTable(
      {
        columns,
        data,
        defaultColumn,
        filterTypes,
        initialState: { pageIndex: 0 },
      },
      useFilters,
      useSortBy,
      usePagination,
      useBlockLayout,
      useResizeColumns,
    );

    return (
      <Grid className={classes.historyTable}>
        <Grid className={classnames(classes.historyTableContainer, undefined)}>
          <table className={classes.historyTableMain} {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      className={[
                        classes.historyHeaderTh,
                        `resizer ${column.isResizing ? 'isResizing' : ''}`,
                      ]}
                      {...column.getResizerProps()}
                    >
                      {column.render('Header') && (
                        <>
                          <tr className={classes.headerTr}>
                            <th
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              className={classes.headerTh}
                            >
                              {column.render('Header')}
                            </th>
                          </tr>
                          <tr className={classes.headerTrFilter}>
                            <th className={classes.headerTh}>
                              {column.canFilter ? column.render('Filter') : null}
                            </th>
                          </tr>
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className={classes.mainTextSectionTbody}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={`history-table-key-${index}`}>
                    {row.cells.map(renderCell)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Grid>
        <Grid container xs={12} className={classes.paginationBtnsContainer}>
          <Grid item xs={4} className={classes.paginationBtns}>
            <button type="button" onClick={() => previousPage(0)} disabled={!canPreviousPage}>
              Previous
            </button>
          </Grid>
          <Grid item xs={2} className={classes.paginationControls}>
            <span>
              Page{'  '}
              <input
                type="number"
                defaultValue={pageIndex + 1}
                value={pageIndex + 1}
                onChange={e => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(page);
                }}
                style={{ width: '100px' }}
              />{' '}
              of {pageOptions.length}
            </span>{' '}
          </Grid>
          <Grid item xs={2} className={classes.paginationControls}>
            <span>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 30, 40, 50].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize} Rows
                  </option>
                ))}
              </select>
            </span>
          </Grid>
          <Grid item xs={4} className={classes.paginationBtns}>
            <button type="button" onClick={() => nextPage()} disabled={!canNextPage}>
              Next
            </button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <ReactTableComp
      data={ptgotNoteHistory}
      columns={[
        {
          Header: 'Progress Note',
          id: 'content',
          minWidth: 797,
          maxWidth: 797,
          accessor: d => getSanitizedValue(d.content),
        },
        {
          Header: 'Updated',
          accessor: 'added',
          minWidth: 400,
          maxWidth: 400,
        },
      ]}
    />
  );
};

const mapStateToProps = state => {
  const { history } = state;
  return {
    history,
  };
};

export default compose(
  connect(mapStateToProps),
  withStyles(styles, { withTheme: true }),
)(HistoryTable);
